import { Box, Flex, Group, Stack, Text } from '@mantine/core'
import { getText, getGenreText } from '../../utils/constants'
import Head from 'next/head'
import Image from 'next/image'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import React, { useEffect, useState, useMemo } from 'react'
import CharacterCarousel from '../../components/character_carousel'
import CrewCarousel from '../../components/crew_carousel'
import ImageFallback from '../../components/image_fallback'
import CountryInput from '../../components/inputs/country_input'
import Layout from '../../components/layout'
import MoreDetails from '../../components/more_details'
import SeasonsCarousel from '../../components/seasons_carousel'
import SimilarCarousel from '../../components/similar_carousel'
import SourcesTabs from '../../components/sources_tabs'
import StreamingAvailability from '../../components/streaming_availability_tabs'
import TrailerButton from '../../components/trailer_button'
import { searchAPI_v2 } from '../../utils/axios'
import { getCountryNameFromISO } from '../../utils/fns'
import { languages } from '../../utils/languages'
import prerenderImportantLocales from '../../utils/prerender_locales.js'

// --- FUNCIONES AUXILIARES ---

// Verifica si la imagen de fondo es de alta calidad
const isHighQualityBackdrop = (imgUrl) => {
  return new Promise((resolve) => {
    const img = new window.Image()
    img.src = imgUrl
    img.onload = () => {
      const isHighQuality = img.width >= 1280
      resolve(isHighQuality)
    }
    img.onerror = () => resolve(false)
  })
}

// Ordena los hits por fecha y retorna el más reciente
const getLatestHits = (hitsData) => {
  if (!hitsData?.hits || !Array.isArray(hitsData.hits) || hitsData.hits.length === 0)
    return null
  const sortedData = [...hitsData.hits].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date)
  )
  return {
    position: sortedData[0]?.position || null,
    hits: sortedData[0]?.hits || null
  }
}

// Función para limpiar valores undefined y convertirlos a null
const cleanUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) return null
  if (typeof obj !== 'object') return obj

  if (Array.isArray(obj)) {
    return obj.map(cleanUndefinedValues)
  }

  const cleaned = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = cleanUndefinedValues(value)
    } else {
      // Para arrays, mantener array vacío en lugar de null
      if (key === 'episodes') {
        cleaned[key] = []
      } else {
        cleaned[key] = null
      }
    }
  }
  return cleaned
}

// Construye el string de metadatos de la serie
const buildMetadata = (series, t_country) => {
  return [
    series.primary_genre ? getGenreText(series.primary_genre) : null,
    series.year,
    series.age_ratings?.US,
    t_country(getCountryNameFromISO(series.primary_country)),
    languages[series.original_language],
    series.runtime ? `${series.runtime} minutes` : null
  ]
    .filter(Boolean)
    .join(' | ')
}

// --- COMPONENTE PRINCIPAL ---

export default function Series({ series, availabilityData, hitsData }) {
  // Destructuring de useRouter y traducciones
  const { locale } = useRouter()
  const t = (key) => getText('Series', key)
  const t_country = (key) => getText('PrimaryCountry', key)

  // Destructuring de datos de la serie
  const {
    translation = {},
    poster,
    logo,
    title,
    synopsis,
    backdrop,
    primary_genre,
    year,
    age_ratings,

    created_by,
    primary_company,
    networks,
    video,
    credits,
    seasons,
    slug,
    uid,
    score
  } = series

  // Estados locales
  const [isLogoBroken, setIsLogoBroken] = useState(false)
  const [blurBackdrop, setBlurBackdrop] = useState(true)
  const [selectedCountry, setSelectedCountry] = useState(
    (locale || 'en-US').split('-')[1]?.toLowerCase() || 'us'
  )
  const [dynamicAvailabilityData, setDynamicAvailabilityData] =
    useState(availabilityData)
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false)

  // Disponibilidad actual según país seleccionado
  const currentAvailability = dynamicAvailabilityData[selectedCountry] || {
    availability: []
  }

  // Memo para los hits más recientes
  const latestHits = useMemo(() => getLatestHits(hitsData), [hitsData])

  // Función para cargar disponibilidad de un país específico
  const loadAvailabilityForCountry = async (countryCode) => {
    if (dynamicAvailabilityData[countryCode]) return // Ya cargado

    setIsLoadingAvailability(true)
    try {
      const response = await fetch(
        `/api/availability?id=${uid}&type=series&country=${countryCode}`
      )
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      setDynamicAvailabilityData((prev) => ({
        ...prev,
        [countryCode]: data
      }))
    } catch (error) {
      console.error(`Error loading availability for ${countryCode}:`, error)
      setDynamicAvailabilityData((prev) => ({
        ...prev,
        [countryCode]: { availability: [] }
      }))
    } finally {
      setIsLoadingAvailability(false)
    }
  }

  // Efecto para verificar calidad del backdrop
  useEffect(() => {
    if (backdrop) {
      isHighQualityBackdrop(backdrop).then((isHighQuality) => {
        setBlurBackdrop(!isHighQuality)
      })
    }
  }, [backdrop])

  // Efecto para cargar disponibilidad cuando cambia el país
  useEffect(() => {
    if (selectedCountry && !dynamicAvailabilityData[selectedCountry]) {
      loadAvailabilityForCountry(selectedCountry)
    }
  }, [selectedCountry, uid, dynamicAvailabilityData, loadAvailabilityForCountry])

  // --- SEO y JSON-LD ---
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    'name': title,
    'image': poster || '/placeholder.jpg',
    'url': `https://www.streamdiscover.com/series/${uid || slug}`,
    'identifier': uid || slug,
    'inLanguage': locale || 'en',
    'description': synopsis || 'No synopsis available.',
    'datePublished': year ? year.toString() : 'N/A',
    'genre': primary_genre || 'Unknown',
    'director':
      series.directors?.map((director) => ({
        '@type': 'Person',
        'name': director
      })) || [],
    'actor':
      credits?.cast?.map((actor) => ({ '@type': 'Person', 'name': actor.name })) ||
      [],
    'creator':
      created_by?.map((creator) => ({ '@type': 'Person', 'name': creator })) || [],
    'productionCompany': primary_company
      ? { '@type': 'Organization', 'name': primary_company }
      : undefined,
    'contentRating': age_ratings?.US || 'Not Rated',
    'aggregateRating': series.ratingValue
      ? {
          '@type': 'AggregateRating',
          'ratingValue': series.ratingValue,
          'bestRating': '10',
          'worstRating': '0',
          'reviewCount': series.reviewCount || '0'
        }
      : undefined,
    'numberOfSeasons': seasons?.length || 0,
    'trailer': video
      ? {
          '@type': 'VideoObject',
          'name': `${title} Trailer`,
          'embedUrl': `https://www.youtube.com/embed/${video.split('=')[1]}`,
          'thumbnailUrl': poster || '/placeholder.jpg',
          'uploadDate': year ? `${year}-01-01` : undefined,
          'description': `Watch the trailer for ${title}`
        }
      : undefined
  }

  // --- RENDER ---
  return (
    <>
      {/* SEO y metadatos */}
      <Head>
        <title>{`${title} - Stream Discover`}</title>
        <meta name="description" content={t('metaDescription', { title })} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        <link href={`https://www.streamdiscover.com/series/${slug}`} />
        <meta property="og:title" content={`${title} - Stream Discover`} />
        <meta property="og:description" content={t('metaDescription', { title })} />
        <meta property="og:type" content="video.tv_show" />
        <meta property="og:image" content={poster || '/placeholder.jpg'} />
        <meta
          property="og:url"
          content={`https://www.streamdiscover.com/${locale}/series/${uid || slug}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} - Stream Discover`} />
        <meta name="twitter:description" content={t('metaDescription', { title })} />
        <meta name="twitter:image" content={poster || '/placeholder.jpg'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      <div>
        {/* Imagen de fondo y gradientes */}
        <Box
          sx={{
            position: 'relative',
            width: '100vw',
            height: blurBackdrop ? '300px' : '380px'
          }}
        >
          <Image
            src={backdrop || '/placeholder.jpg'}
            fill
            alt={title || 'Backdrop image'}
            style={{
              objectFit: 'cover',
              filter: blurBackdrop ? 'blur(80px)' : 'none',
              marginTop: blurBackdrop ? '110px' : '10px'
            }}
            priority
          />
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(to bottom, rgba(8, 17, 24, 1) 5%, rgba(8, 17, 24, 0.7) 20%, rgba(8, 17, 24, 0.6) 40%, rgba(8, 17, 24, 0.6) 60%, rgba(8, 17, 24, 0.7) 80%, rgba(8, 17, 24, 1) 100%)',
              filter: 'blur(200px)'
            }}
          />
          {!blurBackdrop && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  zIndex: 2
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(to bottom, rgba(8, 17, 24, 1) 0%, rgba(8, 17, 24, 0.7) 10%, rgba(8, 17, 24, 0.6) 30%, rgba(8, 17, 24, 0.6) 50%, rgba(8, 17, 24, 0.7) 70%, rgba(8, 17, 24, 1) 101%)',
                  zIndex: 2
                }}
              />
            </>
          )}

          {/* Header principal: poster/logo/título/metadata */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            sx={(theme) => ({
              width: '90vw',
              maxWidth: '1366px',
              margin: '0 auto',
              alignItems: 'center',
              textAlign: 'center',
              [theme.fn.largerThan('md')]: {
                width: '80vw',
                alignItems: 'flex-start',
                textAlign: 'left'
              }
            })}
            pos="relative"
            style={{ zIndex: 3 }}
            pt={{ base: 16, md: 64 }}
            gap={24}
          >
            {/* Poster */}
            <Box
              display={{ base: 'none', md: 'block' }}
              sx={{
                width: '200px',
                height: '300px',
                minWidth: '200px',
                maxWidth: '200px',
                borderRadius: '4px',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <ImageFallback
                src={translation.poster || poster}
                fallbackSrc="/noimage.png"
                alt={title || 'Series poster'}
                priority
              />
            </Box>

            {/* Info principal */}
            <Stack
              spacing={16}
              w={{ base: '100%', md: '80%' }}
              align={{ base: 'center', md: 'flex-start' }}
              sx={{ color: 'white', height: 'auto' }}
            >
              {/* Logo o título */}
              <Box display={{ base: 'none', md: 'block' }}>
                {!isLogoBroken && (translation.logo || logo) ? (
                  <Image
                    src={translation.logo || logo}
                    alt={title || 'Series logo'}
                    width={200}
                    height={100}
                    priority
                    style={{
                      maxWidth: '40%',
                      maxHeight: '100px',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain'
                    }}
                    onError={() => setIsLogoBroken(true)}
                  />
                ) : (
                  <Text size={32} sx={{ maxWidth: '100%' }}>
                    {title}
                  </Text>
                )}
              </Box>

              {/* Mobile: poster y título */}
              <Box
                display={{ base: 'flex', md: 'none' }}
                sx={{
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <Box
                  sx={{
                    width: '200px',
                    height: '300px',
                    borderRadius: '4px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <ImageFallback
                    src={translation.poster || poster}
                    fallbackSrc="/noimage.png"
                    alt={title || 'Series poster'}
                    priority
                  />
                </Box>
                <Stack spacing={8} align="center">
                  <Text size={24} weight={700}>
                    {title}
                  </Text>
                  <Text tt="uppercase" size={12} weight={700}>
                    {buildMetadata(series, t_country)}
                  </Text>
                </Stack>
              </Box>

              {/* Metadata */}
              <Text tt="uppercase" size={12} weight={700}>
                {buildMetadata(series, t_country)}
              </Text>

              {/* Sinopsis */}
              <Text
                size="sm"
                sx={(theme) => ({
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                  textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)',
                  [theme.fn.largerThan('md')]: { textAlign: 'left' }
                })}
              >
                {translation.synopsis || synopsis || t('noSynopsis')}
              </Text>

              {/* Creadores, compañía, network */}
              <Flex gap="xs" wrap="wrap">
                {created_by?.length > 0 && (
                  <Group
                    spacing={4}
                    sx={{ textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)' }}
                  >
                    <Text size="sm" fw="bold">
                      {t('Creators')}:
                    </Text>
                    <Text size="sm">{created_by.join(', ')}</Text>
                  </Group>
                )}
                {primary_company && (
                  <Group
                    spacing={4}
                    sx={{ textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)' }}
                  >
                    <Text size="sm" fw="bold">
                      {t('Company')}:
                    </Text>
                    <Text size="sm">{primary_company}</Text>
                  </Group>
                )}
                {networks?.length > 0 && (
                  <Group
                    spacing={4}
                    sx={{ textShadow: '0px 2px 2px rgba(123, 57, 182, 0.8)' }}
                  >
                    <Text size="sm" fw="bold">
                      {t('Network')}:
                    </Text>
                    <Text size="sm">{networks[0]}</Text>
                  </Group>
                )}
              </Flex>

              {/* Badges de score y hits */}
              {score > 0 && (
                <Flex align="center" gap="md">
                  <Text
                    size={10}
                    weight={700}
                    sx={{
                      backgroundColor: 'rgba(123,57,182,0.2)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      color: 'rgba(123,57,182,1)',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Image
                      src="/scorestar.png"
                      width={10}
                      height={10}
                      alt="Score star"
                    />
                    {Number(score).toFixed(1)}/10
                  </Text>
                  {latestHits && (
                    <>
                      <Text
                        size={10}
                        weight={700}
                        sx={{
                          backgroundColor: 'rgba(123,57,182,0.2)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          color: 'rgba(123,57,182,1)',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        {t('Global Position')}: {Math.floor(latestHits.position)}
                      </Text>
                      {latestHits.hits && (
                        <Text
                          size={10}
                          weight={700}
                          sx={{
                            backgroundColor: 'rgba(123,57,182,0.2)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            color: 'rgba(123,57,182,1)',
                            letterSpacing: '0.5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {t('Hits Score')}: {Math.floor(latestHits.hits)}/100
                        </Text>
                      )}
                    </>
                  )}
                </Flex>
              )}
            </Stack>
          </Flex>
        </Box>

        {/* Contenido principal: trailers, disponibilidad, tabs, carouseles, etc. */}
        <Box
          sx={(theme) => ({
            width: '90vw',
            maxWidth: '1366px',
            margin: '0 auto',
            marginTop: '24px',
            position: 'relative',
            zIndex: 3,
            [theme.fn.largerThan('md')]: {
              width: '80vw',
              marginTop: blurBackdrop ? '16px' : 0
            }
          })}
        >
          <Box
            mt={blurBackdrop ? 60 : 0}
            display={{ base: 'none', md: 'block' }}
            sx={{ width: '200px' }}
          >
            <TrailerButton
              blur={blurBackdrop}
              videoKey={video?.split('=')[1] || null}
            />
          </Box>
          <Stack mt={{ base: 300, md: 10 }}>
            <CountryInput
              sx={(theme) => ({
                width: '100%',
                height: '46px',
                gap: '10px',
                marginLeft: 0,
                fontSize: '10px',
                [theme.fn.largerThan('md')]: { width: '158px', marginLeft: 'auto' }
              })}
              value={selectedCountry}
              onChange={setSelectedCountry}
              isLoading={isLoadingAvailability}
            />
            <StreamingAvailability
              id={uid}
              type="series"
              title={title}
              availabilityData={currentAvailability}
              selectedCountry={selectedCountry}
              isLoading={isLoadingAvailability}
            />
            <SourcesTabs id={uid} type="series" />
            <CharacterCarousel cast={credits.cast} isLoading={false} title={title} />
            <CrewCarousel crew={credits.crew} isLoading={false} title={title} />
            <SeasonsCarousel title={title} seasons={seasons} isLoading={false} />
            <MoreDetails movie={series} title={title} />
            <SimilarCarousel
              slug={slug}
              uid={uid}
              type="series"
              vectorizer="gpt4all"
              title={title}
            />
          </Stack>
        </Box>
      </div>
    </>
  )
}

Series.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete pageName="Serie">
      {page}
    </Layout>
  )
}

Series.propTypes = {
  series: PropTypes.shape({
    title: PropTypes.string.isRequired,
    backdrop: PropTypes.string,
    poster: PropTypes.string,
    logo: PropTypes.string,
    primary_genre: PropTypes.string,
    year: PropTypes.number,
    age_ratings: PropTypes.object,
    primary_country: PropTypes.string,
    original_language: PropTypes.string,
    runtime: PropTypes.number,
    synopsis: PropTypes.string,
    created_by: PropTypes.arrayOf(PropTypes.string),
    primary_company: PropTypes.string,
    networks: PropTypes.arrayOf(PropTypes.string),
    video: PropTypes.string,
    credits: PropTypes.shape({
      cast: PropTypes.array.isRequired,
      crew: PropTypes.array.isRequired
    }).isRequired,
    seasons: PropTypes.array.isRequired,
    slug: PropTypes.string.isRequired,
    uid: PropTypes.string.isRequired
  }).isRequired,
  availabilityData: PropTypes.object.isRequired,
  hitsData: PropTypes.object,
  id: PropTypes.string.isRequired
}

export async function getStaticProps(context) {
  prerenderImportantLocales(context.locale)

  const { id } = context.params
  const isUid = /^[a-zA-Z0-9]{15,}$/.test(id)
  const queryType = isUid ? 'bb' : 'slug'
  const safeLocale = context.locale || 'en-US'
  const language = safeLocale.split('-')[0].toLowerCase()
  const country = safeLocale.split('-')[1]?.toLowerCase() || 'us'

  try {
    const seriesResponse = await searchAPI_v2(
      `/content/metadata/${queryType}?id=${id}&type=series&append=credits%2Cseasons&language=${language}`
    )

    // Fetch hits data for the series
    let hitsData = null
    try {
      const seriesUid = seriesResponse.data.uid
      const hitsResponse = await searchAPI_v2(
        `/content/hits/${seriesUid}?country=${country}&type=series`
      )
      hitsData = hitsResponse.data
    } catch (error) {
      console.error(`Error fetching hits data:`, error)
      hitsData = null
    }

    // Fetch availability for current country only to reduce initial data size
    // Other countries will be loaded on-demand when user changes country
    const availabilityData = {}
    const seriesUid = seriesResponse.data.uid

    try {
      const availabilityUrl = `/content/availability/bb?id=${seriesUid}&type=series&country=${country}`
      const response = await searchAPI_v2(availabilityUrl)
      availabilityData[country] = response.data
    } catch (error) {
      console.error(`Error fetching availability for ${country}:`, error)
      availabilityData[country] = { availability: [] }
    }

    // Limpiar datos de la serie para reducir el tamaño y evitar errores de serialización
    const seriesData = seriesResponse.data

    // Limpiar arrays de créditos
    const cleanCredits = (credits) => {
      if (!credits) return { cast: [], crew: [] }

      const cleanCast = (credits.cast || [])
        .map((actor) => ({
          name: actor.name || '',
          character: actor.character || '',
          profile_path: actor.profile_path || '',
          number_id: actor.number_id || 0,
          role: actor.role || '',
          episodes: actor.episodes || 0
        }))
        .filter((actor) => actor.name)

      const cleanCrew = (credits.crew || [])
        .map((crew) => ({
          name: crew.name || '',
          job: crew.job || '',
          profile_path: crew.profile_path || '',
          number_id: crew.number_id || 0
        }))
        .filter((crew) => crew.name)

      return { cast: cleanCast, crew: cleanCrew }
    }

    // Limpiar temporadas
    const cleanSeasons = (seasons) => {
      if (!seasons || !Array.isArray(seasons)) return []

      return seasons.map((season) => ({
        number: season.number || season.season_number || 0,
        season_number: season.season_number || season.number || 0,
        title: season.title || season.name || '',
        name: season.name || season.title || '',
        release_date: season.release_date || season.air_date || '',
        air_date: season.air_date || season.release_date || '',
        synopsis: season.synopsis || '',
        image: season.image || season.poster_path || '',
        poster_path: season.poster_path || season.image || '',
        episodes: (season.episodes || []).map((episode) => ({
          number: episode.number || 0,
          title: episode.title || '',
          synopsis: episode.synopsis || ''
        }))
      }))
    }

    const cleanedSeriesData = {
      // Campos básicos utilizados en el componente principal
      translation: seriesData.translation || {},
      poster: seriesData.poster || null,
      logo: seriesData.logo || null,
      title: seriesData.title || '',
      synopsis: seriesData.synopsis || '',
      backdrop: seriesData.backdrop || null,
      primary_genre: seriesData.primary_genre || null,
      year: seriesData.year || null,
      age_ratings: seriesData.age_ratings || null,
      primary_country: seriesData.primary_country || null,
      original_language: seriesData.original_language || null,
      runtime: seriesData.runtime || null,
      created_by: seriesData.created_by || [],
      primary_company: seriesData.primary_company || null,
      networks: seriesData.networks || [],
      video: seriesData.video || null,
      slug: seriesData.slug || '',
      uid: seriesData.uid || '',
      score: seriesData.score || null,

      // Campos utilizados en JSON-LD
      directors: seriesData.directors || [],
      ratingValue: seriesData.ratingValue || null,
      reviewCount: seriesData.reviewCount || null,

      // Campos utilizados en MoreDetails
      spoken_languages: seriesData.spoken_languages || [],
      type: seriesData.type || null,
      scripted: seriesData.scripted || null,
      release_date: seriesData.release_date || null,
      hispanic: seriesData.hispanic || null,
      status: seriesData.status || null,
      alternative_titles: (seriesData.alternative_titles || [])
        .filter((title) => title && typeof title === 'string' && title.trim() !== '')
        .map((title) => ({
          title: title,
          country: 'International',
          language: 'Various'
        })),
      genres: seriesData.genres || [],
      keywords: seriesData.keywords || [],
      homepage: seriesData.homepage || null,
      production_companies: seriesData.production_companies || [],

      // Campos de créditos (necesarios para carouseles)
      credits: cleanCredits(seriesData.credits),

      // Temporadas (necesarias para SeasonsCarousel)
      seasons: cleanSeasons(seriesData.seasons)
    }

    return {
      props: {
        series: cleanUndefinedValues(cleanedSeriesData),
        availabilityData,
        hitsData,
        id: id,
        messages: (
          await import(`../../locales/${safeLocale.split('-')[0]}/common.json`)
        ).default
      },
      revalidate: 60 * 60 * 24 * 10
    }
  } catch (error) {
    console.error('Error in getStaticProps:', error)
    return {
      notFound: true
    }
  }
}

export async function getStaticPaths() {
  // Retornar un array vacío para que todas las rutas se generen dinámicamente
  // Esto evita el error de la API y permite que las páginas se generen bajo demanda
  return {
    paths: [],
    fallback: 'blocking'
  }
}
